import { MkIcon } from "@/mk/MkIcon";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Page } from "@/api/client";
import { api } from "@/api/client";
import { useWorkspaceContext } from "@/app/Shell";

interface AnnouncementRow {
    id: number;
    name: string;
    content: string | false;
    publish_date: string | false;
    expiry_date: string | false;
    is_read: boolean;
    announcement_type: "general" | "congratulation" | "condolence";
    occasion: string | false;
}

function moment(value: string | false): Date | null {
    return value ? new Date(`${value.replace(" ", "T")}Z`) : null;
}

/** Shown only between its publish date and its expiry. */
function showing(row: AnnouncementRow, now: Date): boolean {
    const from = moment(row.publish_date);
    const until = moment(row.expiry_date);
    return (!from || from <= now) && (!until || until > now);
}

/** The card and the list carry plain text, never the markup of the body. */
function plain(html: string | false): string {
    if (!html) {
        return "";
    }
    const parsed = new DOMParser().parseFromString(html, "text/html");
    return (parsed.body.textContent || "").replace(/\s+/g, " ").trim();
}

export function HomeAnnouncements() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const client = useQueryClient();
    const context = useWorkspaceContext();
    const [pinnedId, setPinnedId] = useState<number | null>(null);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [clock, setClock] = useState(Date.now());
    const [showList, setShowList] = useState(false);
    const [openListId, setOpenListId] = useState<number | null>(null);
    const [leavingCard, setLeavingCard] = useState<AnnouncementRow | null>(null);
    const previousCard = useRef<AnnouncementRow>();

    const query = useQuery<Page<AnnouncementRow>>({
        queryKey: ["home", "announcements"],
        refetchInterval: 60000,
        queryFn: ({ signal }) => api.listAll<AnnouncementRow>(
            "announcements",
            { status: "published", for_me: true, sort: "-publish_date" },
            signal,
        ),
    });

    const markRead = useMutation({
        mutationFn: (id: number) => api.act("announcements", id, "mark-read"),
        onSuccess: () => client.invalidateQueries({ queryKey: ["home", "announcements"] }),
    });

    const rows = (query.data?.data ?? []).filter((row) => showing(row, new Date(clock)));
    const rotationKey = rows.map((row) => row.id).join(",");
    const pinned = rows.find((row) => row.id === pinnedId);
    const card = pinned ?? (rows.length ? rows[index % rows.length] : undefined);
    useLayoutEffect(() => {
        if (previousCard.current?.id !== card?.id) {
            setLeavingCard(card ? previousCard.current ?? null : null);
        }
        previousCard.current = card;
    }, [card]);
    useEffect(() => {
        if (!leavingCard) return;
        // Also clear the outgoing card when reduced motion disables its animation.
        const timer = window.setTimeout(() => setLeavingCard(null), 750);
        return () => window.clearTimeout(timer);
    }, [leavingCard]);
    useEffect(() => {
        const timer = window.setInterval(() => setClock(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    useEffect(() => {
        if (paused || pinnedId !== null || rows.length < 2) return;
        const timer = window.setInterval(() => setIndex((current) => (current + 1) % rows.length), 6000);
        return () => window.clearInterval(timer);
    }, [paused, pinnedId, rotationKey, rows.length]);
    useEffect(() => {
        if (pinnedId !== null && !rows.some((row) => row.id === pinnedId)) setPinnedId(null);
    }, [rotationKey, pinnedId]);
    const theme = (row: AnnouncementRow) => row.announcement_type === "general" ? ""
        : ` is-greeting is-${row.announcement_type}${row.occasion ? ` is-${row.occasion}` : ""}`;
    const dateOf = (row: AnnouncementRow) => {
        const date = moment(row.publish_date);
        return date ? date.toLocaleDateString(i18n.language, { dateStyle: "medium" }) : "";
    };

    function read(row: AnnouncementRow) {
        if (!row.is_read && !markRead.isPending) {
            markRead.mutate(row.id);
        }
    }

    function toggleCard(row: AnnouncementRow) {
        if (pinned?.id === row.id) {
            setPinnedId(null);
            return;
        }
        setPinnedId(row.id);
        read(row);
    }

    function toggleListRow(row: AnnouncementRow) {
        setOpenListId((current) => (current === row.id ? null : row.id));
        read(row);
    }

    // The heading also provides access to management in an empty database.
    return (
        <div className="pm-home-section pm-home-announcements">
            <div className="pm-home-section-head">
                <h3 className="pm-home-section-title">
                    <MkIcon name="fa-bullhorn" />
                    <span>{t("home.announcements")}</span>
                </h3>
                <div className="pm-home-news-tools">
                    <button type="button" className="pm-home-news-tool"
                            title={t(showList ? "home.news.hideList" : "home.news.showList")}
                            aria-label={t(showList ? "home.news.hideList" : "home.news.showList")}
                            aria-expanded={showList}
                            onClick={() => setShowList((open) => !open)}>
                        <MkIcon name={showList ? "fa-chevron-up" : "fa-list-ul"} />
                        <span>{t(showList ? "home.news.hideList" : "home.news.showList")}</span>
                    </button>
                    {context.data?.can_manage_announcements && (
                        <button type="button" className="pm-home-news-tool is-manage"
                                title={t("home.news.manage")} aria-label={t("home.news.manage")}
                                onClick={() => navigate("/announcements")}>
                            <MkIcon name="fa-cog" />
                            <span>{t("home.news.manage")}</span>
                        </button>
                    )}
                </div>
            </div>

            {card && (
                <div className="pm-home-news-carousel"
                     onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
                     onFocus={() => setPaused(true)}
                     onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}>
                    {leavingCard && (
                        <article key={`leaving-${leavingCard.id}`} aria-hidden="true"
                                 className={`pm-home-news-item pm-home-news-card pm-home-news-leaving${theme(leavingCard)}${leavingCard.is_read ? "" : " is-unread"}`}>
                            <div className="pm-home-news-head">
                                <MkIcon name="fa-bullhorn" />
                                <span className="pm-home-news-title">{leavingCard.name}</span>
                                <span className="pm-home-news-meta"><bdi>{dateOf(leavingCard)}</bdi></span>
                                <MkIcon name="fa-chevron-down" />
                            </div>
                        </article>
                    )}
                    <article key={card.id} className={`pm-home-news-item pm-home-news-card${theme(card)}${card.is_read ? "" : " is-unread"}`}>
                        <button type="button" className="pm-home-news-head"
                                aria-expanded={pinned?.id === card.id}
                                onClick={() => toggleCard(card)}>
                            <MkIcon name="fa-bullhorn" />
                            <span className="pm-home-news-title">{card.name}</span>
                            <span className="pm-home-news-meta"><bdi>{dateOf(card)}</bdi></span>
                            <MkIcon name={pinned?.id === card.id ? "fa-chevron-up" : "fa-chevron-down"} />
                        </button>
                        {pinned?.id === card.id && (
                            <div className="pm-home-news-body">
                                {card.content
                                    ? <div dangerouslySetInnerHTML={{ __html: card.content }} />
                                    : <p>{plain(card.content)}</p>}
                            </div>
                        )}
                    </article>
                </div>
            )}

            {showList && (
                <ul className="pm-home-news pm-home-news-list">
                    {!query.isPending && !query.isError && !rows.length && (
                        <li className="pm-home-news-item" role="status">
                            <div className="pm-home-news-body">{t("home.news.empty")}</div>
                        </li>
                    )}
                    {rows.map((row) => (
                        <li key={row.id}
                            className={`pm-home-news-item${theme(row)}${row.is_read ? "" : " is-unread"}`}>
                            <button type="button" className="pm-home-news-head"
                                    aria-expanded={openListId === row.id}
                                    onClick={() => toggleListRow(row)}>
                                <MkIcon name="fa-bullhorn" />
                                <span className="pm-home-news-title">{row.name}</span>
                                <span className="pm-home-news-meta"><bdi>{dateOf(row)}</bdi></span>
                                <MkIcon name={openListId === row.id ? "fa-chevron-up" : "fa-chevron-down"} />
                            </button>
                            {openListId === row.id && row.content && (
                                <div className="pm-home-news-body"
                                     dangerouslySetInnerHTML={{ __html: row.content }} />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
